import React, { useEffect } from 'react';

const NDAPolicy = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="bg-white">
      {/* Hero Section */}
      <section className="bg-slate-900 text-white py-12 sm:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-3xl sm:text-4xl font-bold mb-4">Non-Disclosure Agreement (NDA) Policy</h1>
          <p className="text-slate-300 text-lg">Effective Date: 01/04/2025</p>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="prose max-w-none">
            <div className="space-y-12">
              {/* Introduction */}
              <div>
                <h2 className="text-2xl font-bold text-slate-900 border-b border-slate-200 pb-4">1. Introduction</h2>
                <p className="text-slate-700 leading-relaxed mt-4">
                  Visrupt ("Company," "we," "our," or "us") is committed to maintaining the confidentiality of client information, trading strategies, and project details. This NDA Policy outlines the conditions under which Non-Disclosure Agreements (NDAs) are offered, executed, and enforced.
                </p>
              </div>

              {/* NDA as an Add-On Service */}
              <div>
                <h2 className="text-2xl font-bold text-slate-900 border-b border-slate-200 pb-4">2. NDA as an Add-On Service</h2>
                <div className="space-y-4 mt-4">
                  <p className="text-slate-700 leading-relaxed">
                    2.1. Visrupt provides NDAs as an optional add-on service, which must be explicitly requested by the client before sharing project details.
                  </p>
                  <p className="text-slate-700 leading-relaxed">
                    2.2. Without an NDA, the Company adheres to its internal confidentiality policies but does not offer additional legal guarantees.
                  </p>
                  <p className="text-slate-700 leading-relaxed">
                    2.3. Once an NDA is signed, the terms of the NDA override any general confidentiality policies, ensuring legally binding protection.
                  </p>
                </div>
              </div>

              {/* Scope of NDA Protection */}
              <div>
                <h2 className="text-2xl font-bold text-slate-900 border-b border-slate-200 pb-4">3. Scope of NDA Protection</h2>
                <div className="space-y-4 mt-4">
                  <p className="text-slate-700 leading-relaxed">
                    3.1. The NDA covers all project-related information shared by the client, including but not limited to:
                  </p>
                  <ul className="list-disc pl-6 text-slate-700">
                    <li>Trading strategies and methodologies</li>
                    <li>Proprietary algorithms and code</li>
                    <li>Business operations and financial details</li>
                    <li>Any other confidential information explicitly mentioned in the NDA</li>
                  </ul>
                  <p className="text-slate-700 leading-relaxed">
                    3.2. The NDA restricts Visrupt from disclosing, sharing, or utilizing client information beyond the scope of the agreed project.
                  </p>
                  <p className="text-slate-700 leading-relaxed">
                    3.3. The confidentiality obligations extend to all employees, subcontractors, and partners working on the project.
                  </p>
                </div>
              </div>

              {/* Exclusions from Confidentiality */}
              <div>
                <h2 className="text-2xl font-bold text-slate-900 border-b border-slate-200 pb-4">4. Exclusions from Confidentiality</h2>
                <div className="space-y-4 mt-4">
                  <p className="text-slate-700 leading-relaxed">
                    4.1. Information shall not be considered confidential if:
                  </p>
                  <ul className="list-disc pl-6 text-slate-700">
                    <li>It is publicly available at the time of disclosure.</li>
                    <li>It is lawfully received from a third party without obligation of confidentiality.</li>
                    <li>It is independently developed by Visrupt without reference to the client's confidential information.</li>
                    <li>It is required to be disclosed by law, provided the client is notified beforehand where legally permissible.</li>
                  </ul>
                  <p className="text-slate-700 leading-relaxed">
                    4.2. Visrupt shall not be held responsible for any third-party leaks, breaches, or disclosures originating from the client's end or any external entities beyond Visrupt's control.
                  </p>
                </div>
              </div>

              {/* NDA Fees and Execution */}
              <div>
                <h2 className="text-2xl font-bold text-slate-900 border-b border-slate-200 pb-4">5. NDA Fees and Execution</h2>
                <div className="space-y-4 mt-4">
                  <p className="text-slate-700 leading-relaxed">
                    5.1. The NDA service is subject to an additional charge, which may be a fixed fee or a percentage of the project value, depending on the complexity and scope of the agreement.
                  </p>
                  <p className="text-slate-700 leading-relaxed">
                    5.2. The NDA must be signed by both parties before any confidential information is shared.
                  </p>
                  <p className="text-slate-700 leading-relaxed">
                    5.3. Visrupt reserves the right to refuse projects requiring an NDA if the terms are deemed unreasonable or beyond the Company's risk tolerance.
                  </p>
                </div>
              </div>

              {/* Duration and Termination */}
              <div>
                <h2 className="text-2xl font-bold text-slate-900 border-b border-slate-200 pb-4">6. Duration and Termination</h2>
                <div className="space-y-4 mt-4">
                  <p className="text-slate-700 leading-relaxed">
                    6.1. The NDA remains in effect for the duration specified in the agreement, typically ranging from one (1) to five (5) years after project completion.
                  </p>
                  <p className="text-slate-700 leading-relaxed">
                    6.2. Upon termination of the NDA, Visrupt will delete or return all confidential materials as per the agreement's terms.
                  </p>
                  <p className="text-slate-700 leading-relaxed">
                    6.3. Obligations under the NDA may continue beyond termination if explicitly stated in the agreement.
                  </p>
                </div>
              </div>

              {/* Breach and Remedies */}
              <div>
                <h2 className="text-2xl font-bold text-slate-900 border-b border-slate-200 pb-4">7. Breach and Remedies</h2>
                <div className="space-y-4 mt-4">
                  <p className="text-slate-700 leading-relaxed">
                    7.1. Any breach of the NDA may result in legal action, including but not limited to financial damages and injunctive relief.
                  </p>
                  <p className="text-slate-700 leading-relaxed">
                    7.2. The client may seek enforcement of the NDA through legal proceedings if necessary.
                  </p>
                </div>
              </div>

              {/* Contact Information */}
              <div>
                <h2 className="text-2xl font-bold text-slate-900 border-b border-slate-200 pb-4">8. Contact Information</h2>
                <div className="mt-4">
                  <p className="text-slate-700 leading-relaxed">
                    For NDA requests or inquiries, please contact us at:
                  </p>
                  <div className="mt-4 space-y-2">
                    <p className="text-slate-700">
                      Email:{' '}
                      <a href="mailto:support@visrupt.in" className="text-blue-600 hover:text-blue-800">
                        support@visrupt.in
                      </a>
                    </p>
                    <p className="text-slate-700">
                      Contact:{' '}
                      <a href="tel:+917522979568" className="text-blue-600 hover:text-blue-800">
                        +91 7522979568
                      </a>
                    </p>
                  </div>
                </div>
              </div>

              {/* Acknowledgment */}
              <div className="bg-slate-50 p-6 rounded-lg border border-slate-200">
                <p className="text-slate-700 font-medium">
                  By requesting an NDA, clients acknowledge and agree to the terms outlined in this NDA Policy.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default NDAPolicy;